import { useEffect, useRef } from 'react'

interface UseAutoGenerateOnceOptions {
  /**
   * While false the latch is cleared, so the next enabled run attempts again. Scopes "once" to a
   * session rather than forever — for a modal, its open state.
   */
  sessionOpen: boolean
  /** While false nothing is attempted and no key is consumed, so a later flip to true still fires. */
  enabled: boolean
  /** One attempt per key. A new key allows a fresh attempt. */
  generationKey: string
  /** Skip generating when the field already holds content. */
  currentValue: string
  /** Skip generating unless the field is idle. */
  isIdle: boolean
  generate: () => void
}

/**
 * Fires `generate` at most once per key per session.
 *
 * The key is consumed as soon as `enabled` passes, before the value and idle checks, so a key that
 * arrives with content already present or a generation in flight gets no second chance. That is
 * deliberate: `generate` writes the value it reads, so the caller would otherwise retrigger itself
 * indefinitely once the field returned to idle still empty.
 */
export function useAutoGenerateOnce({
  sessionOpen,
  enabled,
  generationKey,
  currentValue,
  isIdle,
  generate,
}: UseAutoGenerateOnceOptions) {
  const attemptedKeyRef = useRef<string | null>(null)

  useEffect(() => {
    if (!sessionOpen) {
      attemptedKeyRef.current = null
      return
    }
    if (!enabled) return
    if (attemptedKeyRef.current === generationKey) return
    attemptedKeyRef.current = generationKey
    if (currentValue.trim()) return
    if (!isIdle) return
    generate()
  }, [sessionOpen, enabled, generationKey, currentValue, isIdle, generate])
}
